﻿import { FIQAssist } from "../app";
import { Component, Input, Output, Inject, Attribute, OutputArgs } from "@fiqas/angular-utils";
import { KeyCodes, KeyboardEventCode } from "@fiqas/utils"
import { TranslationService } from "../services/TranslationService";
import { IDisposable } from "../interfaces/general";
import * as Rx from "rxjs/Rx";
import { LogService } from "../services/logService";


export interface DropdownEntry<T> {
    key: T;
    text: string;
}

class DropdownItem<T> implements DropdownEntry<T> {
    key: T;
    text: string;
    selected: boolean;
    hovered: boolean;
}

@Component({
    module: FIQAssist,
    selector: 'dropdownbox',
    template: `<div class="dropdownbox {{$ctrl.childClasses}} ng-class:{multiselect:$ctrl.multiselect, 'has-value': $ctrl.hasValue, error: $ctrl.showError, readonly:$ctrl.readOnly}" id="{{'dropdown_'+$id}}" click-outside="$ctrl.close()"
                    ng-keydown="$ctrl.onKeyDown($event)"
                    ng-class="$ctrl.childClasses"
                    ng-keyup="$ctrl.onKeyUp($event)" >
                <table
                  tooltip-class="customTooltipTB" tooltip-append-to-body="true" tooltip-placement="bottom" tooltip-enable="!$ctrl.opened" uib-tooltip="{{$ctrl.tooltitle}}"
                  tabindex="0" class="selector {{$ctrl.opened ? 'selectopen':'selectclose'}}" ng-click="$ctrl.open($event)" ng-focus="$ctrl.onFocus($event)" ng-blur="$ctrl.onBlur($event)">
                <tr>
                    <td class="selectText ng-class:{'has-clear':$ctrl.showClear}" placeholder="{{$ctrl.hint}}">
                        <span>{{$ctrl.selectText}}</span>
                        <span class="icon-clearField" ng-show="$ctrl.showClear" ng-click="$ctrl.ClearField($event)"></span>
                        <span class="mandatory-dot" ng-if="$ctrl.mandatory" uib-tooltip="{{'FIELD_REQUIRED_TOOLTIP' | translate}}" tooltip-placement="bottom" tooltip-class="customTooltipTB" tooltip-append-to-body="true"></span>
                    </td>
                    <td rowspan="2" class="icon" ng-show="$ctrl.isEditable"><span class="{{$ctrl.opened ? 'icon-dropUP':'icon-dropdown'}}">
                        <span class="path1"></span><span class="path2"></span></span>
                    </td>
                </tr>
                <tr>
                    <td class="border"></td>
                </tr>
                </table>
                <div class="droplist" ng-show="$ctrl.opened">
                    <div class="droplistitem" ng-class="[{selected:item.selected},{hovered:item.hovered}]" ng-repeat="item in $ctrl._items" ng-mousedown="$ctrl.itemclick($event, item.key)">
                        <span class="label">{{item.text}}</span>
                    </div>
                </div>`
})

export class DropdownBox<T> {
    constructor(
        @Inject('$timeout') private $timeout: ng.ITimeoutService,
        @Inject('$rootScope') private $rootScope: angular.IRootScopeService,
        @Inject() private translationService: TranslationService,
        @Inject('$element') private $element: angular.IRootElementService,
        @Inject('$scope') private $scope: angular.IScope,
        @Inject() private logService: LogService
    ) {
    }

    private isInitialized: boolean = false;
    private _items: DropdownItem<T>[];
    private hoveredItem: DropdownItem<T> = null;
    private _prevselected: any;
    private _selected: T[] = [];
    private _parent: Element;
    private disposables: IDisposable[] = [];
    private wasFocused = false;
    private hasFocus = false;

    $onDestroy() {
        this.disposables.forEach(d => {
            try { d() } catch (e) { this.logService.warning('DropDownBox', 'Error disposing', e); }
        });
    }

    @Input() public hint: any = null;

    private _mandatory;
    @Attribute() public get mandatory() {
        return this._mandatory;
    }
    public set mandatory(val: boolean | string) {
        this._mandatory = val === true || val === "true";
    }

    private _childClasses;
    @Attribute() public get childClasses() {
        return this._childClasses;
    }
    public set childClasses(val: string) {
        this._childClasses = val;
    }

    private _showClear;
    @Attribute() public get showClear() {
        return this._showClear;
    }
    public set showClear(val: boolean | string) {
        this._showClear = val === true || val === "true";
    }
    private _tooltitle;
    @Attribute() public get tooltitle() {
        return (this.opened) ? "" : this._tooltitle;
    }
    public set tooltitle(val: string) {
        this._tooltitle = val;
    }
    private get hasValue() {
        return this._selected && this._selected.length > 0;
    }
    private get showError() {
        return this.mandatory && ((!this.hasFocus && this.wasFocused && !this.hasValue) || (!this.hasFocus && this.iserror));
    }

    private ClearField($event: BaseJQueryEventObject) {
        this.selected = [];
        $event.preventDefault();
        $event.stopPropagation();
    }

    private onSearch: Rx.Subject<string>;

    public $onInit() {

        this.onSearch = new Rx.Subject();
        var searchString = "";
        var onSearchSubscription = this.onSearch
            .map(x => searchString += x)
            .debounceTime(400)
            .map(x => { searchString = ""; return x; })
            .subscribe({
                next: result => {
                    this.Search(result);
                    this.$scope.$apply();
                }
            });
        this.disposables.push(() => onSearchSubscription.unsubscribe());

        this.isInitialized = true;
        this.updateSelection(false);
        if (this.items.length > 0 && this.translate)
            this.items.forEach(item => {
                var sub = this.translationService.TranslateObservable(item.text).subscribe(t => item.text = t);
                this.disposables.push(() => sub.unsubscribe())
            });
        this.translationService.TranslateAsync(this.multiselecttext || "MULTIPLE_ITEMS_SELECTED").then(t => this.multiselecttext = t);
        this.translationService.TranslateAsync(this.nonselecttext || "").then(t => this.nonselecttext = t);
    }

    @Input() public get items(): DropdownEntry<T>[] {
        return this._items;
    }
    public set items(value: DropdownEntry<T>[]) {
        this._items = [];
        value.forEach(x => {
            var item = { key: x.key, text: x.text, hovered: false, selected: false };
            if (this.isInitialized && this.translate) {
                var sub = this.translationService.TranslateObservable(item.text).subscribe(t => item.text = t);
                this.disposables.push(() => sub.unsubscribe());
            }
            this._items.push(item);
        });
        if (this._items !== undefined && this._items.length == 1)
            this._selected = this._items.map(x => x.key);
        this.updateSelection(true);
    }

    @Input() public get selected(): T[] {
        return this._selected;
    }

    public set selected(value: T[]) {
        if (value === undefined)
            return;

        this._selected = value;
        this.updateSelection(true);
    }

    @Input() public readOnly: boolean;
    @Input() public iserror: boolean;
    @Input() public multiselect: boolean;
    @Input() public multiselecttext: string;
    @Input() public nonselecttext: string;
    @Input() public focusinput: boolean;
    @Attribute() public translate: boolean;
    @Output() public onselectionchanged: OutputArgs<{ selected: T[] }>;

    private _selectText: string = '';
    public get selectText(): string {
        if (this._items !== undefined) {
            this._items.forEach(x => x.selected = false);
            var sel = this._items.filter(x => this._selected.some(y => y === x.key));
            sel.forEach(x => x.selected = true);
            return sel.length == 0 ? this.nonselecttext : sel.length == 1 ? sel[0].text : this.multiselecttext;
        }
    }
    public opened: boolean = false;

    public open(event) {
        //event.stopPropagation();
        if (this.isEditable) {
            this.opened = !this.opened;
            if (!this.opened) {
                this.updateSelection(true);
            } else {
                this.scrollToViewAsync();
            }
        }
    }

    private onFocus($event: FocusEvent) {
        this.hasFocus = this.wasFocused = true;
        this._items.forEach(x => {
            x.selected = false; x.hovered = false;
        });
        if (this._selected) {
            var sel = this._items.filter(x => this._selected.some(y => y === x.key));
            sel.forEach(x => x.selected = true);
            if (sel.length > 0) {
                this.hoveredItem = sel[0];
                this.hoveredItem.hovered = true;
            }
        }

        this.scrollToViewAsync();
    }

    async $postLink() {
        if (this.focusinput) {
            await this.$timeout(25);
            this.focusField();
        }
    }

    private focusField() {
        var element = this.$element;
        var field = (<any>element[0].getElementsByClassName('selector').item(0));
        if (field) {
            field.focus();
        }

    }

    private async onBlur($event: FocusEvent) {
        this.hasFocus = false;
        await this.$timeout();
        this.opened = false;
    }

    @Output() public valueSubmit: (args: { selected: T[] }) => void;
    private onKeyUp($event: KeyboardEvent) {
        if ($event.keyCode === 0) {
            switch ($event.code) {
                case KeyboardEventCode.upArrow:
                case KeyboardEventCode.downArrow:
                    $event.preventDefault();
                    break;
                case KeyboardEventCode.enter:
                    $event.preventDefault();
                    if (this.opened) {
                        this.opened = false;
                    }
                    this.valueSubmit({ selected: this.selected });
                    break;
                case KeyboardEventCode.escape:
                    $event.preventDefault();
                    this.opened = false;
                    this.$element[0].querySelector('.dropdownbox .selector').focus();
                    break;
                default:
                    if (this.isEditable) {
                        var ucChar = $event.keyCode;
                        if (ucChar >= 65 && ucChar <= 90) {
                            $event.preventDefault();
                            this.onSearch.next(String.fromCharCode(ucChar));
                        }
                    }
                    break;
            }

        } else {
            switch ($event.keyCode) {
                case KeyCodes.upArrow:
                case KeyCodes.downArrow:
                    $event.preventDefault();
                    break;
                case KeyCodes.enter:
                    $event.preventDefault();
                    if (this.opened) {
                        this.opened = false;
                    }
                    this.valueSubmit({ selected: this.selected });
                    break;
                case KeyCodes.escape:
                    $event.preventDefault();
                    this.opened = false;
                    this.$element[0].querySelector('.dropdownbox .selector').focus();
                    break;
                default:
                    if (this.isEditable) {
                        var ucChar = $event.keyCode;
                        if (ucChar >= 65 && ucChar <= 90) {
                            $event.preventDefault();
                            this.onSearch.next(String.fromCharCode(ucChar));
                        }
                    }
                    break;
            }
        }
    }
    private onKeyDown($event: KeyboardEvent) {
        if ($event.keyCode === 0) {
            switch ($event.code) {
                case KeyboardEventCode.upArrow:
                    if (this.isEditable) {
                        $event.preventDefault();
                        if (this.opened) {
                            this.move(-1);
                        }

                    }
                    break;
                case KeyboardEventCode.downArrow:
                    if (this.isEditable) {
                        $event.preventDefault();
                        if (!this.opened) {
                            this.open(null);
                            if (!this.selected.length) {
                                setTimeout(() => this.move(1), 0);
                            }
                        } else {
                            this.move(1);
                        }
                    }
                    break;

                case KeyboardEventCode.space:
                    if (this.isEditable) {
                        $event.preventDefault();
                        this.select(false);
                    }
                    break;
                case KeyboardEventCode.tab:
                    if (this.opened) {
                        this.opened = false;
                    }
                    break;

                case KeyboardEventCode.c:
                    if (!$event.ctrlKey && !$event.metaKey) {
                        $event.preventDefault();
                    }
                    break;

                case KeyboardEventCode.enter:
                case KeyboardEventCode.space:
                default:
                    $event.preventDefault();
                    break;
            }

        } else {
            switch ($event.keyCode) {
                case KeyCodes.upArrow:
                    if (this.isEditable) {
                        $event.preventDefault();
                        if (this.opened) {
                            this.move(-1);
                        }

                    }
                    break;
                case KeyCodes.downArrow:
                    if (this.isEditable) {
                        $event.preventDefault();
                        if (!this.opened) {
                            this.open(null);
                            if (!this.selected.length) {
                                setTimeout(() => this.move(1), 0);
                            }
                        } else {
                            this.move(1);
                        }
                    }
                    break;

                case KeyCodes.space:
                    if (this.isEditable) {
                        $event.preventDefault();
                        this.select(false);
                    }
                    break;
                case KeyCodes.tab:
                    if (this.opened) {
                        this.opened = false;
                    }
                    break;

                case KeyCodes.c:
                    if (!$event.ctrlKey && !$event.metaKey) {
                        $event.preventDefault();
                    }
                    break;

                case KeyCodes.enter:
                case KeyCodes.space:
                default:
                    $event.preventDefault();
                    break;
            }
        }
    }

    private Search(searchString: string) {
        this.logService.debug('DropDownBox', `Search: ${searchString}`);
        var selection = this._items.filter(x => x.text && x.text.toUpperCase().indexOf(searchString.toUpperCase()) === 0);
        if (selection.length >= 0) {
            if (this.hoveredItem != null)
                this.hoveredItem.hovered = false;
            this.hoveredItem = selection[0];
            if (this.hoveredItem != null)
                this.hoveredItem.hovered = true;
            if (this.opened) {
                this.scrollToViewAsync();
            } else {
                this.select(false);
            }
        }
    }

    private move(direction: number) {
        if (this._items.length > 0) {
            var curIndex = this.hoveredItem ? this._items.indexOf(this.hoveredItem) : -1;
            var newindex = Math.max(0, Math.min(this._items.length - 1, curIndex + direction));
            if (this.hoveredItem != null)
                this.hoveredItem.hovered = false;
            this.hoveredItem = this._items[newindex];
            this.hoveredItem.hovered = true;
            this.scrollToViewAsync();
        }
    }

    private select(keepOpen: boolean) {
        if (this.hoveredItem) {
            if (this.multiselect && this.opened) {
                if (this.hoveredItem && this.selected.some(x => x === this.hoveredItem.key)) {
                    this.selected = this.selected.filter(x => x != this.hoveredItem.key);
                } else {
                    this.selected.push(this.hoveredItem.key);
                }
                if (!keepOpen) {
                    this.opened = false;
                    this.$element[0].querySelector('.dropdownbox .selector').focus();
                }
            } else {
                this.selected = [this.hoveredItem.key];
                if (!keepOpen) {
                    this.opened = false;
                    this.$element[0].querySelector('.dropdownbox .selector').focus();
                }
            }
        }
    }

    public close() {
        if (this.opened) {
            this.opened = false;
            this.updateSelection(true);
        }
    }

    private updateSelection(withEvent: boolean): void {
        if (this.isInitialized) {
            if (!this.arraysEqual(this._selected, this._prevselected)) {
                this._prevselected = this._selected.filter(x => true);
                if (withEvent && !this.readOnly) {
                    this.onselectionchanged({ selected: this.selected });
                }
            }
        }
    }

    protected arraysEqual(a: any[], b: any[]): boolean {
        if (a && b) {
            return a.length === b.length && a.every(x => b.some(y => y === x));
        }
        return false;
    }


    public get isEditable(): boolean {
        return !this.readOnly && this._items !== undefined && this._items.length > 1;
    }

    public itemclick(event: JQueryEventObject, key: any) {
        if (event.button === 0) {
            event.preventDefault();

            if (this.hoveredItem != null)
                this.hoveredItem.hovered = false;
            this.hoveredItem = this._items.filter(x => x.key === key).shift();
            this.hoveredItem.hovered = true;

            if (this.multiselect && event.ctrlKey) {
                if (this.selected.some(x => x === key)) {
                    this.selected = this.selected.filter(x => x != key);
                } else {
                    this.selected = this.selected.concat([key]);
                }
            } else {
                this.selected = [key];
            }
            this.opened = this.multiselect && event.ctrlKey;
        }
    }

    public imagePicture(checked: boolean): string {
        return checked ? "css/images/checkliston.png" : "css/images/checklistoff.png";
    }

    private async scrollToViewAsync() {
        await this.$timeout();
        this.scrollToView();
    }

    private async scrollToView() {
        if (this.opened) {
            var parent = this.$element[0].getElementsByClassName('droplist').item(0);
            var s = <any>(parent.getElementsByClassName('hovered').item(0));
            if (s)
                parent.scrollTop = Math.max(0, s.offsetTop - ((parent.clientHeight - s.clientHeight) / 2));
        }
    }
}
